
<script setup lang="ts">
import HomeContentMainTitle from "./HomeContentMainTitle.vue";
import HomeContentCarousel from "./HomeContentCarousel.vue";
import HomeContentDarkTheme from "./HomeContentDarkTheme.vue";
import HomeContentOpportunity from "./HomeContentOpportunity.vue";
import HomeContentAccordion from "./HomeContentAccordion.vue";
import HomeContentProducts from "./HomeContentProducts.vue";
import HomeContentBanner from "./HomeContentBanner.vue";

import { useWindowScroll, useScroll } from '@vueuse/core'
import { onMounted, ref, inject, onUnmounted, watch } from "vue";

import 'vue3-carousel/dist/carousel.css'
import debounce from "@/helpers/debounce"

const main = ref<HTMLElement>()
const view = inject<any>("view", "mobile")

const screenIndex = ref<number>(0)
const screens = ref<HTMLElement[]>([])
const fullpageScroll = ref<boolean>(true)
const triggerY = ref<number>(0)

const body = ref<HTMLElement>()
const activeScroll = ref<boolean>(false) 

const { y } = useWindowScroll()
const { directions } = useScroll(document)

const onScrollStop = debounce(() => activeScroll.value = false, 200)

const watchers: {  
  unwatchY: any, 
  unwatchFullpage: any
} = {
  unwatchY: undefined, 
  unwatchFullpage: undefined
}


const moveTo = (i: number) => {
  if (activeScroll.value) {
    return
  }
  
  activeScroll.value = true;

  if (!i) {
    window.scrollTo({top: 0, behavior: "smooth"})
  } else {
    const screen = screens.value[i]
    screen.scrollIntoView({behavior: "smooth"});
  }
  screenIndex.value = i
}

const moveBy = (i: number) => {
  if (screenIndex.value + i > 2 || activeScroll.value || y.value > triggerY.value) {
    return
  }
  screenIndex.value += i;
  
  moveTo(screenIndex.value)
}

const wheelScroll = (event: WheelEvent) => { 
  if (fullpageScroll.value) {
    event.preventDefault();
  }

  if (Math.abs(event.deltaY) < 30) {
    return
  } else if (event.deltaY > 0) {
    moveBy(1);
  } else if (event.deltaY < 0 && screenIndex.value > 0) {
    moveBy(-1)
  }

  onScrollStop();
}

const keydownScroll = (event: KeyboardEvent) => {
  if (event.key == "ArrowDown") {
    moveBy(1)
  } else if (event.key == "ArrowUp" && screenIndex.value > 0) {
    moveBy(-1)
  }

  onScrollStop();
}

const desktopFullpageScroll = () => {
  if (!screens.value) {
    return
  }  

  const unwatchY = watch(y, () => {
    if (y.value < triggerY.value + 60 && !fullpageScroll.value && directions.top) {
      fullpageScroll.value = true;
      setTimeout(() => moveTo(1), 600);
    } else if (y.value >= triggerY.value && fullpageScroll.value && directions.bottom) {
      fullpageScroll.value = false
    }
  })

  const unwatchFullpage = watch(fullpageScroll, () => {
    if (fullpageScroll.value) {
      body.value?.classList.add('overflow-hidden')
    } else {
      body.value?.classList.remove('overflow-hidden')
    }
  })
  
  document.addEventListener("wheel", wheelScroll, { passive:false })
  document.addEventListener("keydown", keydownScroll)

  return { unwatchY, unwatchFullpage }
}

onMounted(() => {
  main.value?.childNodes.forEach((item) => {
    screens.value?.push(item as HTMLElement)
  })
  triggerY.value = screens.value[2].offsetTop - 20
  body.value = document.body

  if (view.value == "desktop") {
    const initWatchers = desktopFullpageScroll();
    watchers.unwatchY = initWatchers?.unwatchY
    watchers.unwatchFullpage = initWatchers?.unwatchFullpage
  }
})

watch(view, () => {
  if (view.value == "desktop") {
    body.value?.classList.add("overflow-hidden")
    const initWatchers = desktopFullpageScroll();
    watchers.unwatchY = initWatchers?.unwatchY
    watchers.unwatchFullpage = initWatchers?.unwatchFullpage
  } else {
    watchers.unwatchY()
    watchers.unwatchFullpage()
    body.value?.classList.remove("overflow-hidden")
  }
})

onUnmounted(() => {
  document.removeEventListener("wheel", wheelScroll)
  document.removeEventListener("keydown", keydownScroll)
}) 

</script>
<template>
  <main ref="main">
    <HomeContentMainTitle />
    <HomeContentOpportunity />
    <HomeContentDarkTheme />
    <HomeContentCarousel />
    <HomeContentBanner />
    <HomeContentAccordion />
    <HomeContentProducts />
  </main>
</template>